import type { OfferId } from '@/api/schema'
import { AspectType } from '@/api/services/sweph-service/types'

export const FREE_LIMIT = 2

export const DAY_LIMIT = 500

export const BOT_VALUES = ['zoe', 'milana', 'eva'] as const

export const PRICE_CHANGE_DEPLOYED_AT = new Date('2026-07-03T05:57:29Z')

export type Pricing = {
  messages: Record<OfferId, number>
  synastries: Record<OfferId, number>
  matrixYear: Record<OfferId, number>
  amount: Record<OfferId, number>
  loyaltyAmount: Record<OfferId, number>
  loyaltyMessages: Record<OfferId, number>
}

export const LEGACY_PRICING: Pricing = {
  synastries: {
    micro: 1,
    one: 1,
    week: 3,
    full: 5,
  },
  matrixYear: {
    micro: 0,
    one: 0,
    week: 0,
    full: 0,
  },
  amount: {
    micro: 199,
    one: 199,
    week: 490,
    full: 790,
  },
  messages: {
    micro: 50,
    one: 50,
    week: 200,
    full: 400,
  },
  loyaltyAmount: {
    micro: 199,
    one: 199,
    week: 490,
    full: 790,
  },
  loyaltyMessages: {
    micro: 50,
    one: 50,
    week: 200,
    full: 400,
  },
}

export const CURRENT_PRICING: Pricing = {
  synastries: {
    micro: 0,
    one: 0,
    week: 0,
    full: 0,
  },
  matrixYear: {
    micro: 0,
    one: 0,
    week: 0,
    full: 0,
  },
  amount: {
    micro: 99,
    one: 249,
    week: 590,
    full: 990,
  },
  messages: {
    micro: 10,
    one: 40,
    week: 150,
    full: 300,
  },
  loyaltyAmount: {
    micro: 99,
    one: 249,
    week: 590,
    full: 990,
  },
  loyaltyMessages: {
    micro: 10,
    one: 40,
    week: 150,
    full: 300,
  },
}

export const SIGNS_IN: Record<string, string> = {
  'Овен': 'Овне',
  'Телец': 'Тельце',
  'Близнецы': 'Близнецах',
  'Рак': 'Раке',
  'Лев': 'Льве',
  'Дева': 'Деве',
  'Весы': 'Весах',
  'Скорпион': 'Скорпионе',
  'Стрелец': 'Стрельце',
  'Козерог': 'Козероге',
  'Водолей': 'Водолее',
  'Рыбы': 'Рыбах',
}

export enum ARCANA_ID {
  MAGICIAN = 1,
  HIGH_PRIESTESS = 2,
  EMPRESS = 3,
  EMPEROR = 4,
  HIEROPHANT = 5,
  LOVERS = 6,
  CHARIOT = 7,
  JUSTICE = 8,
  HERMIT = 9,
  WHEEL_OF_FORTUNE = 10,
  STRENGTH = 11,
  HANGED_MAN = 12,
  DEATH = 13,
  TEMPERANCE = 14,
  DEVIL = 15,
  TOWER = 16,
  STAR = 17,
  MOON = 18,
  SUN = 19,
  JUDGEMENT = 20,
  WORLD = 21,
  FOOL = 22,
}

export const ARCANA_SLUG: Record<number, string> = {
  [ARCANA_ID.MAGICIAN]: 'magician',
  [ARCANA_ID.HIGH_PRIESTESS]: 'high-priestess',
  [ARCANA_ID.EMPRESS]: 'empress',
  [ARCANA_ID.EMPEROR]: 'emperor',
  [ARCANA_ID.HIEROPHANT]: 'hierophant',
  [ARCANA_ID.LOVERS]: 'lovers',
  [ARCANA_ID.CHARIOT]: 'chariot',
  [ARCANA_ID.JUSTICE]: 'justice',
  [ARCANA_ID.HERMIT]: 'hermit',
  [ARCANA_ID.WHEEL_OF_FORTUNE]: 'wheel-of-fortune',
  [ARCANA_ID.STRENGTH]: 'strength',
  [ARCANA_ID.HANGED_MAN]: 'hanged-man',
  [ARCANA_ID.DEATH]: 'death',
  [ARCANA_ID.TEMPERANCE]: 'temperance',
  [ARCANA_ID.DEVIL]: 'devil',
  [ARCANA_ID.TOWER]: 'tower',
  [ARCANA_ID.STAR]: 'star',
  [ARCANA_ID.MOON]: 'moon',
  [ARCANA_ID.SUN]: 'sun',
  [ARCANA_ID.JUDGEMENT]: 'judgement',
  [ARCANA_ID.WORLD]: 'world',
  [ARCANA_ID.FOOL]: 'fool',
}

export const MATRIX_ARCANA: Record<number, { name: string; tarot: number }> = {
  [ARCANA_ID.MAGICIAN]: { name: 'Маг', tarot: 1 },
  [ARCANA_ID.HIGH_PRIESTESS]: { name: 'Жрица', tarot: 2 },
  [ARCANA_ID.EMPRESS]: { name: 'Императрица', tarot: 3 },
  [ARCANA_ID.EMPEROR]: { name: 'Император', tarot: 4 },
  [ARCANA_ID.HIEROPHANT]: { name: 'Иерофант', tarot: 5 },
  [ARCANA_ID.LOVERS]: { name: 'Влюблённые', tarot: 6 },
  [ARCANA_ID.CHARIOT]: { name: 'Колесница', tarot: 7 },
  [ARCANA_ID.JUSTICE]: { name: 'Справедливость', tarot: 11 },
  [ARCANA_ID.HERMIT]: { name: 'Отшельник', tarot: 9 },
  [ARCANA_ID.WHEEL_OF_FORTUNE]: { name: 'Колесо Фортуны', tarot: 10 },
  [ARCANA_ID.STRENGTH]: { name: 'Сила', tarot: 8 },
  [ARCANA_ID.HANGED_MAN]: { name: 'Повешенный', tarot: 12 },
  [ARCANA_ID.DEATH]: { name: 'Смерть', tarot: 13 },
  [ARCANA_ID.TEMPERANCE]: { name: 'Умеренность', tarot: 14 },
  [ARCANA_ID.DEVIL]: { name: 'Дьявол', tarot: 15 },
  [ARCANA_ID.TOWER]: { name: 'Башня', tarot: 16 },
  [ARCANA_ID.STAR]: { name: 'Звезда', tarot: 17 },
  [ARCANA_ID.MOON]: { name: 'Луна', tarot: 18 },
  [ARCANA_ID.SUN]: { name: 'Солнце', tarot: 19 },
  [ARCANA_ID.JUDGEMENT]: { name: 'Суд', tarot: 20 },
  [ARCANA_ID.WORLD]: { name: 'Мир', tarot: 21 },
  [ARCANA_ID.FOOL]: { name: 'Шут', tarot: 0 },
}

export enum ZodiacSign {
  ARIES = 'ARIES',
  TAURUS = 'TAURUS',
  GEMINI = 'GEMINI',
  CANCER = 'CANCER',
  LEO = 'LEO',
  VIRGO = 'VIRGO',
  LIBRA = 'LIBRA',
  SCORPIO = 'SCORPIO',
  SAGITTARIUS = 'SAGITTARIUS',
  CAPRICORN = 'CAPRICORN',
  AQUARIUS = 'AQUARIUS',
  PISCES = 'PISCES',
}

export const ZODIAC_SIGNS = [
  ZodiacSign.ARIES,
  ZodiacSign.TAURUS,
  ZodiacSign.GEMINI,
  ZodiacSign.CANCER,
  ZodiacSign.LEO,
  ZodiacSign.VIRGO,
  ZodiacSign.LIBRA,
  ZodiacSign.SCORPIO,
  ZodiacSign.SAGITTARIUS,
  ZodiacSign.CAPRICORN,
  ZodiacSign.AQUARIUS,
  ZodiacSign.PISCES,
]

export const SIGN_NAMES: Record<ZodiacSign, string> = {
  [ZodiacSign.ARIES]: 'Овен',
  [ZodiacSign.TAURUS]: 'Телец',
  [ZodiacSign.GEMINI]: 'Близнецы',
  [ZodiacSign.CANCER]: 'Рак',
  [ZodiacSign.LEO]: 'Лев',
  [ZodiacSign.VIRGO]: 'Дева',
  [ZodiacSign.LIBRA]: 'Весы',
  [ZodiacSign.SCORPIO]: 'Скорпион',
  [ZodiacSign.SAGITTARIUS]: 'Стрелец',
  [ZodiacSign.CAPRICORN]: 'Козерог',
  [ZodiacSign.AQUARIUS]: 'Водолей',
  [ZodiacSign.PISCES]: 'Рыбы',
}

export const SIGN_EMOJIS: Record<ZodiacSign, string> = {
  [ZodiacSign.ARIES]: '♈',
  [ZodiacSign.TAURUS]: '♉',
  [ZodiacSign.GEMINI]: '♊',
  [ZodiacSign.CANCER]: '♋',
  [ZodiacSign.LEO]: '♌',
  [ZodiacSign.VIRGO]: '♍',
  [ZodiacSign.LIBRA]: '♎',
  [ZodiacSign.SCORPIO]: '♏',
  [ZodiacSign.SAGITTARIUS]: '♐',
  [ZodiacSign.CAPRICORN]: '♑',
  [ZodiacSign.AQUARIUS]: '♒',
  [ZodiacSign.PISCES]: '♓',
}

export const ASPECT_SYMBOLS: Record<number, string> = {
  [AspectType.CONJUNCTION]: '☌',
  [AspectType.SEXTILE]: '⚹',
  [AspectType.SQUARE]: '□',
  [AspectType.TRINE]: '△',
  [AspectType.OPPOSITION]: '☍',
}

export const ASPECT_NAMES: Record<number, string> = {
  [AspectType.CONJUNCTION]: 'соединение',
  [AspectType.SEXTILE]: 'секстиль',
  [AspectType.SQUARE]: 'квадрат',
  [AspectType.TRINE]: 'трин',
  [AspectType.OPPOSITION]: 'оппозиция',
}

export enum HelpSections {
  LoginExistingAccount = 'login-existing-account',
  SwitchAccount = 'switch-account',
  EmailCodeNotReceived = 'email-code-not-received',
  PurchaseAccountEmail = 'purchase-account-email',
  PaidPackageNotShowing = 'paid-package-not-showing',
  DataAndCalculationsMissing = 'data-and-calculations-missing',
}
